import jsPDF from "jspdf";

/**
 * Generate a single-page landscape PDF for investor decks.
 * Compact, designed and dense — hero 3D image + site plan + headline stats + investment summary.
 */
export async function exportInvestorPdf(building, summary, buildingId, snaps, siteSummary = null) {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "letter" });
  const W = doc.internal.pageSize.getWidth();   // 792
  const H = doc.internal.pageSize.getHeight();  // 612

  // ----- Palette -----
  const bgDark = [11, 15, 20];
  const card   = [15, 23, 42];
  const accent = [252, 196, 25];
  const text   = [230, 237, 243];
  const muted  = [148, 163, 184];
  const good   = [34, 197, 94];
  const cyan   = [34, 211, 238];

  // Background
  doc.setFillColor(...bgDark); doc.rect(0, 0, W, H, "F");

  // Top accent bar
  doc.setFillColor(...accent); doc.rect(0, 0, W, 4, "F");

  // ----- Header -----
  doc.setFont("courier", "bold"); doc.setFontSize(9);
  doc.setTextColor(...accent);
  doc.text("INVESTOR ONE-PAGER · SUNRISE COMMUNITY · BELIZE", 36, 28);

  doc.setFont("helvetica", "bold"); doc.setFontSize(26);
  doc.setTextColor(...text);
  doc.text("Sunrise Community", 36, 60);

  doc.setFont("helvetica", "normal"); doc.setFontSize(11);
  doc.setTextColor(...muted);
  const _hdrUnits = siteSummary?.total_residential ?? 192;
  const _hdrComm = siteSummary?.total_commercial ?? 8;
  doc.text(`Container Housing · 2.13-Acre Mixed-Use Development · ${_hdrUnits} Units + ${_hdrComm} Commercial Storefronts`, 36, 78);

  // Date / version on right
  const today = new Date().toISOString().slice(0, 10);
  doc.setFont("courier", "normal"); doc.setFontSize(8);
  doc.setTextColor(...muted);
  doc.text(`v${today.replace(/-/g, ".")} · CONFIDENTIAL`, W - 36, 28, { align: "right" });

  // Pull live site numbers (with safe fallback to spec)
  const totalRes = siteSummary?.total_residential ?? 192;
  const totalComm = siteSummary?.total_commercial ?? 8;
  const totalBldgs = siteSummary?.total_buildings ?? 6;
  const totalContainers = siteSummary?.total_containers ?? 152;
  const livingSF = siteSummary?.total_living_sf ?? 46080;
  const parkingTotal = siteSummary?.parking_required ?? 93;

  // ----- Headline stats strip (5 columns) -----
  const stats = [
    { label: "RESIDENTIAL UNITS", val: totalRes, color: accent },
    { label: "COMMERCIAL UNITS",  val: totalComm,   color: accent },
    { label: "BUILDINGS",         val: totalBldgs,   color: accent },
    { label: "CONTAINERS",        val: totalContainers, color: accent },
    { label: "PARKING STALLS",    val: parkingTotal, color: cyan },
  ];
  const stripY = 100;
  const colW = (W - 72) / stats.length;
  stats.forEach((s, i) => {
    const x = 36 + i * colW;
    doc.setFillColor(...card); doc.rect(x, stripY, colW - 6, 56, "F");
    doc.setDrawColor(...s.color); doc.setLineWidth(0.5); doc.rect(x, stripY, colW - 6, 56, "S");
    doc.setFont("courier", "normal"); doc.setFontSize(7);
    doc.setTextColor(...muted);
    doc.text(s.label, x + 10, stripY + 14);
    doc.setFont("helvetica", "bold"); doc.setFontSize(22);
    doc.setTextColor(...s.color);
    doc.text(String(s.val), x + 10, stripY + 42);
  });

  // ----- Hero 3D image (left) + Site plan (right) -----
  const imgY = 175;
  const imgH = 280;
  const imgGap = 12;
  const imgW = (W - 72 - imgGap) / 2;

  const drawImageBox = async (x, dataURL, label, ringColor) => {
    doc.setFillColor(...card); doc.rect(x, imgY, imgW, imgH, "F");
    doc.setDrawColor(...ringColor); doc.setLineWidth(0.7); doc.rect(x, imgY, imgW, imgH, "S");
    // label
    doc.setFont("courier", "bold"); doc.setFontSize(8);
    doc.setTextColor(...ringColor);
    doc.text(label, x + 10, imgY + 14);

    if (dataURL) {
      try {
        const img = new Image();
        img.src = dataURL;
        await new Promise((res) => { img.onload = res; img.onerror = res; });
        const iw = img.naturalWidth || 1, ih = img.naturalHeight || 1;
        const padTop = 22, pad = 10;
        const aW = imgW - pad * 2;
        const aH = imgH - padTop - pad;
        const scale = Math.min(aW / iw, aH / ih);
        const dW = iw * scale, dH = ih * scale;
        const dX = x + (imgW - dW) / 2;
        const dY = imgY + padTop + (aH - dH) / 2;
        doc.addImage(dataURL, "PNG", dX, dY, dW, dH, undefined, "FAST");
      } catch (err) {
        console.warn("Investor PDF: failed to embed image snapshot, continuing without it:", err);
      }
    }
  };

  await drawImageBox(36, snaps?.hero3d, "3D MASSING · 6-BAR STACKS LAYOUT", accent);
  await drawImageBox(36 + imgW + imgGap, snaps?.site, "OPTIMIZED SITE PLAN · STACKS + GATEWAY", cyan);

  // ----- Bottom: investment highlights + unit mix -----
  const bottomY = imgY + imgH + 14;
  const bottomH = H - bottomY - 24;

  // Left: highlights
  const lW = (W - 72) * 0.6 - 6;
  doc.setFillColor(...card); doc.rect(36, bottomY, lW, bottomH, "F");
  doc.setDrawColor(...accent); doc.setLineWidth(0.5); doc.rect(36, bottomY, lW, bottomH, "S");
  doc.setFont("courier", "bold"); doc.setFontSize(9);
  doc.setTextColor(...accent);
  doc.text("INVESTMENT HIGHLIGHTS", 50, bottomY + 16);

  const highlights = [
    "• Modular ISBU container construction — fast build, fixed-cost manufacturing",
    `• ${totalRes} residential + ${totalComm} commercial mixed-use on 2.13 acres (high-density ${Math.round(totalRes / 2.13)} units/ac)`,
    `• Golf-cart-primary mobility · EV-equipped stalls · ${parkingTotal} stalls total per Belize SI 157/2022`,
    "• Continuous catwalk + canopy on entry side · individual balconies on courtyard side",
    "• Optional rooftop PV solar over catwalks + balcony canopies — energy-positive potential",
    "• Dual exterior steel stairs per long wing · IRC R310 compliant · 3-story HC modules",
  ];
  doc.setFont("helvetica", "normal"); doc.setFontSize(9);
  doc.setTextColor(...text);
  highlights.forEach((line, i) => {
    doc.text(line, 50, bottomY + 34 + i * 14);
  });

  // Right: unit mix
  const rX = 36 + lW + 12;
  const rW = (W - 72) - lW - 12;
  doc.setFillColor(...card); doc.rect(rX, bottomY, rW, bottomH, "F");
  doc.setDrawColor(...cyan); doc.setLineWidth(0.5); doc.rect(rX, bottomY, rW, bottomH, "S");
  doc.setFont("courier", "bold"); doc.setFontSize(9);
  doc.setTextColor(...cyan);
  doc.text("UNIT MIX PER BUILDING", rX + 14, bottomY + 16);

  doc.setFont("courier", "normal"); doc.setFontSize(8.5);
  const ut_map = summary.by_type;
  building.unit_types.forEach((u, i) => {
    const y = bottomY + 34 + i * 13;
    const hex = u.color.replace("#", "");
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    doc.setFillColor(r, g, b); doc.rect(rX + 14, y - 7, 8, 8, "F");
    doc.setTextColor(...text);
    doc.text(u.label, rX + 28, y);
    doc.setTextColor(...muted);
    doc.text(`${u.length_ft}'×${u.depth_ft}'`, rX + 110, y);
    doc.setTextColor(...accent);
    doc.text(`×${ut_map[u.id] || 0}`, rX + rW - 14, y, { align: "right" });
  });

  // Footer
  doc.setFont("courier", "normal"); doc.setFontSize(7);
  doc.setTextColor(...muted);
  doc.text("SHEET I1.0 · INVESTOR ONE-PAGER · FOR PRELIMINARY DISCUSSION ONLY", 36, H - 12);
  doc.setTextColor(...good);
  doc.text(`${building.name.toUpperCase()} · ${today}`, W - 36, H - 12, { align: "right" });

  doc.save(`sunrise_investor_one_pager_${today.replace(/-/g, "")}.pdf`);
}
